import { useState, useMemo, useCallback, useEffect } from "react";

/**
 * Client-side sort hook for master-data tables.
 *
 * Usage:
 *   const { sorted, sortKey, sortDir, toggle } = useTableSort(rows, {
 *     initialKey: "name",
 *     storageKey: "sos-sort-workers",   // persists last-used sort in localStorage
 *     getValue: (row, key) => key === "client" ? clientNameLookup(row) : row[key],
 *   });
 *
 * Then pass `sortKey`, `sortDir`, and `toggle` into <SortableTh /> headers.
 * Clicking the same column flips asc → desc → asc. Clicking a new column
 * always starts ascending. Undefined / null values sort as empty string.
 *
 * When `storageKey` is provided, the current sort key + direction are saved
 * to `localStorage[storageKey]` and rehydrated on next mount, so opening a
 * fresh browser starts where the user last left off.
 */
export function useTableSort(
  rows,
  { getValue, initialKey = null, initialDir = "asc", storageKey } = {},
) {
  // Rehydrate from localStorage on first render if a storageKey is provided.
  const [sortKey, setSortKey] = useState(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.key === "string") return parsed.key;
        }
      } catch { /* ignore corrupted json */ }
    }
    return initialKey;
  });
  const [sortDir, setSortDir] = useState(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && (parsed.dir === "asc" || parsed.dir === "desc")) return parsed.dir;
        }
      } catch { /* ignore */ }
    }
    return initialDir;
  });

  // Persist any change to localStorage.
  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ key: sortKey, dir: sortDir }),
      );
    } catch { /* private mode or quota */ }
  }, [sortKey, sortDir, storageKey]);

  const toggle = useCallback(
    (key) => {
      if (key === sortKey) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey],
  );

  const sorted = useMemo(() => {
    if (!sortKey || !Array.isArray(rows)) return rows || [];
    const get = getValue || ((r, k) => r?.[k]);
    return [...rows].sort((a, b) => {
      const av = get(a, sortKey);
      const bv = get(b, sortKey);
      const an =
        av == null ? "" : typeof av === "string" ? av.toLowerCase() : av;
      const bn =
        bv == null ? "" : typeof bv === "string" ? bv.toLowerCase() : bv;
      if (an < bn) return sortDir === "asc" ? -1 : 1;
      if (an > bn) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sortKey, sortDir, getValue]);

  return { sorted, sortKey, sortDir, toggle };
}
